import { HTMLAttributes, MouseEvent, useState } from 'react';
import { apiChangePassword } from '@/dashboard/api/user';
import { useDisplayUserInfo } from '@/dashboard/hooks/useDisplayUserInfo';
import { useMaterialMedia } from '@/dashboard/hooks/useMaterialMedia';
import { useProjectNavigate } from '@/dashboard/hooks/useProjectNavigate';
import { Divider } from '@/dashboard/mui';
import { getSettingGeneralPath, getSettingIndexPath } from '@/dashboard/utils/routes';
import { useAuth0 } from '@auth0/auth0-react';
import { UserCircleIcon } from '@heroicons/react/outline';
import { removeAllJoveAppAuthKeys } from '@jove-frontend/utils';
import { Close, Email, Logout, Settings } from '@mui/icons-material';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import clsx from 'clsx';
import toast from 'react-hot-toast';

type PersonalCenterDropDownMenuProps = HTMLAttributes<HTMLDivElement>;

const PersonalCenterDropDownMenu = (props: PersonalCenterDropDownMenuProps) => {
  const { className } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const { logout } = useAuth0();
  const { isMobile } = useMaterialMedia();
  const { displayUserInfo } = useDisplayUserInfo();
  const { push } = useProjectNavigate();

  /**
   * requests
   */
  const requestChangePassword = async () => {
    toast.loading('Sending a password change request');
    const [res] = await apiChangePassword({});

    if (res) {
      toast.success("We've just sent you an email to reset your password.");
    }
  };

  /**
   * methods
   */
  const clickAction = (e: MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  // close action
  const closeAction = () => {
    setAnchorEl(null);
  };

  // logout
  const logoutAction = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    await removeAllJoveAppAuthKeys();
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  // setting action
  const settingAction = () => {
    if (isMobile) {
      push({ path: getSettingIndexPath() });
    } else {
      push({ path: getSettingGeneralPath() });
    }
  };

  const open = !!anchorEl;
  return (
    <Box className={clsx(className)}>
      <IconButton onClick={clickAction} size="small">
        <UserCircleIcon className="h-7 w-7" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        id="account-user-menu"
        open={open}
        onClose={closeAction}
        onClick={closeAction}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {displayUserInfo?.first_name && displayUserInfo?.last_name && (
          <Box className="flex flex-col">
            <MenuItem className="min-w-[300px]">
              <Box className="relative flex w-full flex-col items-center py-1">
                <Typography className="font-bold" variant="h6">
                  {displayUserInfo?.first_name} {displayUserInfo?.last_name}
                </Typography>
                <Typography className="font-bold" variant="subtitle1">
                  {displayUserInfo?.email}
                </Typography>
                <IconButton className="absolute right-[-12px] top-[-6px]">
                  <Close />
                </IconButton>
              </Box>
            </MenuItem>
            <Divider />
          </Box>
        )}

        <MenuItem onClick={settingAction}>
          <IconButton>
            <Settings />
          </IconButton>
          Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={requestChangePassword}>
          <IconButton>
            <Email />
          </IconButton>
          Change password
        </MenuItem>
        <Divider />
        <MenuItem className="min-w-[300px]" onClick={logoutAction} disabled={loggingOut}>
          <IconButton disabled={loggingOut}>
            <Logout />
          </IconButton>
          {loggingOut ? 'Logging out...' : 'Logout'}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default PersonalCenterDropDownMenu;
